import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from 'routes';
import { useTranslation } from 'react-i18next';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { getCommonCountries } from 'store/common/selectors';
import {
  getOrgLoading,
  getOrgMappedOrganisations,
} from 'store/organisation/selectors';
import { getReturnData } from 'store/returns/selectors';
import { getVatPrepLoading } from 'store/vat/selectors';
import { EntityComponent } from 'components/Entity';
import { DrawerType, LicenseType } from 'store/app/enums';
import { clearMappingData, turnDrawerOn } from 'store/app/actions';
import { DrawerData } from 'store/app/types';
import { ChooseReturn } from 'components/MappingForm/choose-return';
import { fetchAddReturnDataRequest } from 'store/returns/actions';
import { ConnectEntity } from 'components/ConnectEntity';
import { InviteUser } from 'components/UsersComponent/invite-user';
import { CreateGroup } from 'components/CreateGroup';
import { Entity } from 'store/organisation/models';
import { getUserLoading, getUserMembers } from 'store/user/selectors';
import { useState, useRef } from 'react';
import { useLocation } from 'react-router';
import { checkPageLoaded } from 'utils/check-page-loaded';
import {
  Preference,
  PreferenceActions,
  usePreferences,
  PreferenceSections,
} from 'utils/preferences';
import { getBillingHasAdvancedRights } from 'store/billing/selectors';
import { updateCurrentUserPreferencesRequest } from 'store/auth/actions';
import { returnMappingEditPath } from 'utils/tifd-mapping';

export const PAGE_SIZE_ENTITIES = 'page-size-entities';
/** Persisted list vs card layout for the entities page (`list` | `card`). */
export const ENTITIES_VIEW_LAYOUT = 'entities-view-layout';
/** Persisted visible / locked / ordered columns for the entities table. */
export const ENTITIES_COLUMNS_CONFIG = 'entities-columns-config';

export const OrganisationsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const mappedOrganisations = useSelector(getOrgMappedOrganisations);
  const countries = useSelector(getCommonCountries);
  const returnData = useSelector(getReturnData);
  const currentUser = useSelector(getAuthUser);
  const orgLoading = useSelector(getOrgLoading);
  const vatLoading = useSelector(getVatPrepLoading);
  const members = useSelector(getUserMembers);
  const userLoading = useSelector(getUserLoading);
  const loading = orgLoading || vatLoading;
  const [showPage, setShowPage] = useState(false);
  const hasAdvancedUserRightsPre = useSelector(getBillingHasAdvancedRights);
  const userLicence = useSelector(getAuthUserLicence);
  const [hasAdvancedUserRights, setHasAdvancedUserRights] =
    useState<boolean>(false);
  const hasDispatchedPreferences = useRef(false);
  const preferences = usePreferences();
  React.useEffect(() => {
    setHasAdvancedUserRights(
      hasAdvancedUserRightsPre || userLicence?.licenseType === LicenseType.TRIAL
    );
  }, [hasAdvancedUserRightsPre, userLicence]);

  const handlePreferences = (
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser)
      return preferences(
        action,
        PreferenceSections.ENTITIES,
        preference,
        currentUser?.userUuid
      );
    return false;
  };

  const addEntity = () => {
    const drawerData: DrawerData = {
      title: t('organisation.add-new-entity'),
      subtitle: t('organisation.connect-entity-subtitle'),
      type: DrawerType.CONNECT_ENTITY,
      component: <ConnectEntity />,
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const createGroup = () => {
    const drawerData: DrawerData = {
      title: t('group.create-group'),
      subtitle: t('group.create-group-subtitle'),
      type: DrawerType.CREATE_GROUP,
      component: <CreateGroup entities={mappedOrganisations || []} />,
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const editGroup = (entity: Entity) => {
    const drawerData: DrawerData = {
      title: t('group.edit-group'),
      subtitle: t('group.create-group-subtitle'),
      type: DrawerType.CREATE_GROUP,
      component: (
        <CreateGroup entities={mappedOrganisations || []} editGroup={entity} />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  React.useEffect(() => {
    setShowPage(true);
    currentUser && checkPageLoaded(location.pathname, routes.organisations.all);
  }, []);

  const addReturnMapping = (connectionId: string) => {
    if (connectionId) {
      const drawerData: DrawerData = {
        title: t('organisation.add-tax-return'),
        type: DrawerType.ADD_TAX_RETURN,
        component: (
          <ChooseReturn editMode={false} setSectionTouched={() => {}} />
        ),
      };
      if (!returnData || returnData.connectionId !== connectionId) {
        dispatch(fetchAddReturnDataRequest(connectionId));
      }
      dispatch(clearMappingData());
      dispatch(turnDrawerOn(drawerData));
    } else alert(t('organisation.missing-connection-id'));
  };

  const editReturnMapping = (connectionId: string, mappingId: string) => {
    if (connectionId && mappingId) {
      dispatch(clearMappingData());
      const returnTypeId = mappedOrganisations
        ?.flatMap((entity) => entity.returns || [])
        .find((mappedReturn) => mappedReturn.mappingId === mappingId)
        ?.returnTypeId;
      navigate(returnMappingEditPath(connectionId, mappingId, returnTypeId));
    } else alert(t('organisation.missing-connection-id'));
  };

  const openMemberWindow = (entity: Entity) => {
    const modalData: DrawerData = {
      type: DrawerType.INVITE_USER,
      title: `${t('common.share')} “${entity.organisation} ”`,
      component: (
        <InviteUser
          loading={userLoading}
          members={entity.users} //@ts-ignore
          allUsers={_.differenceBy(members, entity.users, 'accountUuid')}
          organisationId={entity.organisationId}
        />
      ),
    };

    dispatch(turnDrawerOn(modalData));
  };

  const editUsers = (entity: Entity) => {
    openMemberWindow(entity);
  };

  return (
    <React.Fragment>
      {showPage && (
        <>
          <Helmet>
            <title>{`${t('organisation.entities')} | ${process.env.PROJECT_NAME}`}</title>
            <meta
              name="description"
              content={t('organisation.entities-page-description')}
            />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            ></meta>
          </Helmet>
          <EntityComponent
            loading={loading}
            mappedOrganisations={mappedOrganisations}
            countries={countries}
            addReturnMapping={addReturnMapping}
            editReturnMapping={editReturnMapping}
            addEntity={addEntity}
            createGroup={createGroup}
            editGroup={editGroup}
            editUsers={editUsers}
            currentUser={currentUser}
            handlePreferences={handlePreferences}
            hasAdvancedUserRights={hasAdvancedUserRights}
          />
        </>
      )}
    </React.Fragment>
  );
};
